import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProperties } from '../../hooks/useProperties';
import MapContainerComponent from '../map/MapContainer';
import {
  fetchPropertyListings,
  fetchListingReservations,
  fetchReservationOffer,
  offerRevisionAction,
  reservationAction,
  fetchListingSchedules,
  addScheduleSlot,
  deleteScheduleSlot,
  updateSaleDetails,
  updateRentDetails,
} from '../../services/listingService';

// ─── constants ────────────────────────────────────────────────────────────────

const PROP_STATUS_LABEL = {
  DRAFT: 'پیش‌نویس', SUBMITTED: 'ارسال شده', ACTIVE: 'فعال',
  INACTIVE: 'غیرفعال', SOLD: 'فروخته شده', RENTED: 'اجاره داده شده',
};

const LISTING_STATUS_LABEL = {
  DRAFT: 'پیش‌نویس', SUBMITTED: 'ارسال شده', HIDDEN: 'پنهان',
  ACTIVE: 'فعال', INACTIVE: 'غیرفعال', SOLD: 'فروخته شده', RENTED: 'اجاره داده شده',
};

const RES_STATUS = {
  PEND:        { text: 'در انتظار پاسخ',  color: '#f57c00', bg: '#fff3e0' },
  NEGOTIATING: { text: 'در حال مذاکره',   color: '#1565c0', bg: '#e3f2fd' },
  ACCEPTED:    { text: 'پذیرفته شده',     color: '#2e7d32', bg: '#e8f5e9' },
  REJECTED:    { text: 'رد شده',           color: '#c62828', bg: '#ffebee' },
  COMPLETED:   { text: 'نهایی شده',        color: '#2e7d32', bg: '#e8f5e9' },
  CANCELLED:   { text: 'لغو شده',          color: '#757575', bg: '#f5f5f5' },
};

// New offer status values are lowercase
const OFFER_STATUS = {
  pending_response:  { text: 'در انتظار پاسخ شما', color: '#f57c00', bg: '#fff3e0' },
  changes_requested: { text: 'تغییرات درخواست شده', color: '#1565c0', bg: '#e3f2fd' },
  accepted:          { text: 'پذیرفته شده',          color: '#2e7d32', bg: '#e8f5e9' },
  rejected:          { text: 'رد شده',               color: '#c62828', bg: '#ffebee' },
  withdrawn:         { text: 'پس گرفته شده',          color: '#757575', bg: '#f5f5f5' },
  expired:           { text: 'منقضی شده',             color: '#757575', bg: '#f5f5f5' },
  cancelled:         { text: 'لغو شده',               color: '#757575', bg: '#f5f5f5' },
};

const FURNISHED_MAP = {
  UNFURNISHED: 'بدون مبلمان', SEMI_FURNISHED: 'نیمه مبله', FULLY_FURNISHED: 'کاملاً مبله',
};

const DAY_LABELS = {
  0: 'دوشنبه', 1: 'سه‌شنبه', 2: 'چهارشنبه',
  3: 'پنج‌شنبه', 4: 'جمعه', 5: 'شنبه', 6: 'یکشنبه',
};
const TIME_SLOTS = ['08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00'];
const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];

// ─── OffersPanel ──────────────────────────────────────────────────────────────
// Actions (accept/reject) now target the current_revision via /dealbridge/offer-revisions/{id}/{action}/

function OffersPanel({ reservationId, onActioned }) {
  const [offer, setOffer] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  const load = useCallback(async () => {
    try { setOffer(await fetchReservationOffer(reservationId)); } catch { /* ignore */ }
  }, [reservationId]);

  useEffect(() => { load(); }, [load]);

  const doRevisionAction = async (revisionId, action, body) => {
    setActionLoading(true);
    try {
      await offerRevisionAction(revisionId, action, body || {});
      await load();
      if (onActioned) onActioned();
    } catch (e) {
      alert((e && e.response && e.response.data && e.response.data.detail) || 'خطا در انجام عملیات');
    } finally { setActionLoading(false); setShowRejectForm(false); setRejectNote(''); }
  };

  if (!offer) return null;

  const revision = offer.current_revision;
  const badge = OFFER_STATUS[offer.status] || { text: offer.status, color: '#333', bg: '#eee' };
  const canAct = revision &&
    revision.status === 'submitted' &&
    revision.created_by_role === 'buyer' &&
    offer.status === 'pending_response';

  return (
    <div className="offers-panel">
      <div className="offers-panel-title"> پیشنهاد قیمت</div>
      <div className="offer-card">
        <div className="offer-card-top">
          <span className="offer-badge" style={{ background: badge.bg, color: badge.color }}>{badge.text}</span>
        </div>
        {revision && (
          <div className="offer-details">
            {revision.asking_price && <div className="offer-row"><span>قیمت پیشنهادی:</span><span>{Number(revision.asking_price).toLocaleString('fa-IR')} تومان</span></div>}
            {revision.deposit      && <div className="offer-row"><span>ودیعه:</span><span>{Number(revision.deposit).toLocaleString('fa-IR')} تومان</span></div>}
            {revision.monthly_rent && <div className="offer-row"><span>اجاره ماهانه:</span><span>{Number(revision.monthly_rent).toLocaleString('fa-IR')} تومان</span></div>}
          </div>
        )}
        {canAct && (
          <div className="offer-actions">
            <button className="res-btn res-btn-accept" disabled={actionLoading} onClick={() => doRevisionAction(revision.id, 'accept')}>
              {actionLoading ? '...' : '✓ پذیرش پیشنهاد'}
            </button>
            <button className="res-btn res-btn-reject" disabled={actionLoading} onClick={() => setShowRejectForm(true)}>
              ✕ رد پیشنهاد
            </button>
          </div>
        )}
        {showRejectForm && (
          <div className="reservation-reject-form">
            <textarea className="reservation-reject-input" rows={2} placeholder="دلیل رد (اختیاری)"
              value={rejectNote} onChange={e => setRejectNote(e.target.value)} />
            <div className="reservation-reject-actions">
              <button className="res-btn res-btn-reject" disabled={actionLoading}
                onClick={() => doRevisionAction(revision.id, 'reject', {})}>
                {actionLoading ? '...' : 'تأیید رد'}
              </button>
              <button className="res-btn res-btn-cancel" onClick={() => { setShowRejectForm(false); setRejectNote(''); }}>انصراف</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
// ─── ReservationRequestsPanel ─────────────────────────────────────────────────

function ReservationRequestsPanel({ listingId }) {
  const [reservations, setReservations] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [rejectingId, setRejectingId] = useState(null);

  const load = useCallback(async () => {
    try { setReservations(await fetchListingReservations(listingId)); } catch { /* ignore */ }
  }, [listingId]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (resId, action, body) => {
    setActionLoading(resId);
    try {
      await reservationAction(resId, action, body || {});
      await load();
    } catch (e) {
      alert((e && e.response && e.response.data && e.response.data.detail) || 'خطا در انجام عملیات');
    } finally { setActionLoading(null); setRejectingId(null); setRejectNote(''); }
  };

  if (reservations.length === 0) return null;

  const PAST = ['COMPLETED', 'CANCELLED', 'REJECTED'];
  const active = reservations.filter(r => !PAST.includes(r.status));
  const past   = reservations.filter(r =>  PAST.includes(r.status));

  return (
    <div className="reservation-panel">
      <div className="reservation-panel-title">
        📋 درخواست‌های رزرو
        {active.length > 0 && <span className="reservation-panel-badge">{active.length}</span>}
      </div>
      {active.map(r => {
        const badge     = RES_STATUS[r.status] || { text: r.status, color: '#333', bg: '#eee' };
        const busy      = actionLoading === r.id;
        const buyerName = (r.buyer && (r.buyer.full_name || r.buyer.username)) || ('خریدار #' + String(r.id).slice(0, 6));
        return (
          <div key={r.id} className="reservation-card">
            <div className="reservation-card-top">
              <span className="reservation-buyer">👤 {buyerName}</span>
              <span className="reservation-status-badge" style={{ background: badge.bg, color: badge.color }}>{badge.text}</span>
            </div>
            {r.note && <p className="reservation-note">یادداشت: {r.note}</p>}
            <div className="reservation-actions">
              {r.status === 'PEND' && (
                <>
                  <button className="res-btn res-btn-accept" disabled={busy} onClick={() => doAction(r.id, 'accept')}>
                    {busy ? '...' : '✓ پذیرش و شروع مذاکره'}
                  </button>
                  <button className="res-btn res-btn-reject" disabled={busy} onClick={() => setRejectingId(r.id)}>
                    ✕ رد درخواست
                  </button>
                </>
              )}
              {r.status === 'NEGOTIATING' && (
                <>
                  <button className="res-btn res-btn-reject" disabled={busy} onClick={() => setRejectingId(r.id)}>
                    ✕ رد درخواست
                  </button>
                </>
              )}
              {r.status === 'ACCEPTED' && <span className="res-waiting">در انتظار تأیید نهایی خریدار</span>}
            </div>
            {rejectingId === r.id && (
              <div className="reservation-reject-form">
                <textarea className="reservation-reject-input" rows={2} placeholder="دلیل رد (اختیاری)"
                  value={rejectNote} onChange={e => setRejectNote(e.target.value)} />
                <div className="reservation-reject-actions">
                  <button className="res-btn res-btn-reject" disabled={busy}
                    onClick={() => doAction(r.id, 'reject', { rejection_note: rejectNote })}>
                    {busy ? '...' : 'تأیید رد'}
                  </button>
                  <button className="res-btn res-btn-cancel" onClick={() => { setRejectingId(null); setRejectNote(''); }}>انصراف</button>
                </div>
              </div>
            )}
            {(r.status === 'NEGOTIATING' || r.status === 'ACCEPTED') && (
              <OffersPanel reservationId={r.id} onActioned={load} />
            )}
          </div>
        );
      })}
      {past.length > 0 && (
        <details className="reservation-past">
          <summary>درخواست‌های قبلی ({past.length})</summary>
          {past.map(r => {
            const badge     = RES_STATUS[r.status] || { text: r.status, color: '#333', bg: '#eee' };
            const buyerName = (r.buyer && (r.buyer.full_name || r.buyer.username)) || ('خریدار #' + String(r.id).slice(0, 6));
            return (
              <div key={r.id} className="reservation-card reservation-card-past">
                <span className="reservation-buyer">👤 {buyerName}</span>
                <span className="reservation-status-badge" style={{ background: badge.bg, color: badge.color }}>{badge.text}</span>
              </div>
            );
          })}
        </details>
      )}
    </div>
  );
}

// ─── ListingEditModal ─────────────────────────────────────────────────────────

function ListingEditModal({ listing, onClose, onSaved }) {
  const isSale = listing.listing_type === 'SALE';
  const [askingPrice,     setAskingPrice]     = useState((listing.sale_details && listing.sale_details.asking_price) || '');
  const [monthlyRent,     setMonthlyRent]     = useState((listing.rent_details && listing.rent_details.monthly_rent) || '');
  const [deposit,         setDeposit]         = useState((listing.rent_details && listing.rent_details.deposit) || '');
  const [furnishedStatus, setFurnishedStatus] = useState((listing.rent_details && listing.rent_details.furnished_status) || 'UNFURNISHED');
  const [mortgageStatus,  setMortgageStatus]  = useState((listing.sale_details && listing.sale_details.mortgage_status) || 'NONE');
  const [hasTenant,       setHasTenant]       = useState((listing.sale_details && listing.sale_details.has_tenant) || false);
  const [schedules,        setSchedules]        = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  const loadSchedules = useCallback(async () => {
    setLoadingSchedules(true);
    try { setSchedules(await fetchListingSchedules(listing.id)); } catch { /* non-fatal */ }
    setLoadingSchedules(false);
  }, [listing.id]);

  useEffect(() => { loadSchedules(); }, [loadSchedules]);

  const isSlotSelected = (weekday, start) =>
    schedules.some(s => s.weekday === weekday && s.start_time === start + ':00');

  const toggleSlot = async (weekday, start) => {
    const end = TIME_SLOTS[TIME_SLOTS.indexOf(start) + 1];
    if (!end) return;
    const existing = schedules.find(s => s.weekday === weekday && s.start_time === start + ':00');
    try {
      if (existing) await deleteScheduleSlot(listing.id, existing.id);
      else await addScheduleSlot(listing.id, { weekday, start_time: start, end_time: end });
      await loadSchedules();
    } catch { /* non-fatal */ }
  };

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      if (isSale) {
        await updateSaleDetails(listing.id, { asking_price: askingPrice || null, mortgage_status: mortgageStatus, has_tenant: hasTenant });
      } else {
        await updateRentDetails(listing.id, { monthly_rent: monthlyRent || null, deposit: deposit || null, furnished_status: furnishedStatus });
      }
      onSaved(); onClose();
    } catch (e) {
      setError((e && e.response && e.response.data && (e.response.data.detail || JSON.stringify(e.response.data))) || 'خطا در ذخیره');
    }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content listing-edit-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isSale ? 'ویرایش آگهی فروش' : 'ویرایش آگهی اجاره'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {error && <div className="error-message">⚠️ {error}</div>}
          <div className="edit-section">
            <h4 className="edit-section-title"> قیمت و شرایط</h4>
            {isSale ? (
              <>
                <div className="field-group">
                  <label>قیمت فروش (تومان)</label>
                  <input type="number" className="text-input" value={askingPrice}
                    onChange={e => setAskingPrice(e.target.value)} placeholder="قیمت درخواستی" />
                </div>
                <div className="field-group">
                  <label>وضعیت وام</label>
                  <div className="option-group">
                    {[['NONE', 'ندارد'], ['AVAILABLE', 'دارد']].map(([v, l]) => (
                      <label key={v} className={'option-item' + (mortgageStatus === v ? ' selected' : '')}>
                        <input type="radio" checked={mortgageStatus === v} onChange={() => setMortgageStatus(v)} />
                        <span>{l}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="field-group">
                  <label>وضعیت مستاجر</label>
                  <div className="option-group">
                    {[[false, 'خالی'], [true, 'دارای مستاجر']].map(([v, l]) => (
                      <label key={String(v)} className={'option-item' + (hasTenant === v ? ' selected' : '')}>
                        <input type="radio" checked={hasTenant === v} onChange={() => setHasTenant(v)} />
                        <span>{l}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="field-group">
                  <label>اجاره ماهانه (تومان)</label>
                  <input type="number" className="text-input" value={monthlyRent}
                    onChange={e => setMonthlyRent(e.target.value)} placeholder="مبلغ اجاره" />
                </div>
                <div className="field-group">
                  <label>ودیعه (تومان)</label>
                  <input type="number" className="text-input" value={deposit}
                    onChange={e => setDeposit(e.target.value)} placeholder="مبلغ رهن" />
                </div>
                <div className="field-group">
                  <label>وضعیت مبلمان</label>
                  <div className="option-group">
                    {[['UNFURNISHED', 'بدون مبلمان'], ['SEMI_FURNISHED', 'نیمه مبله'], ['FULLY_FURNISHED', 'کاملاً مبله']].map(([v, l]) => (
                      <label key={v} className={'option-item' + (furnishedStatus === v ? ' selected' : '')}>
                        <input type="radio" checked={furnishedStatus === v} onChange={() => setFurnishedStatus(v)} />
                        <span>{l}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="edit-section">
            <h4 className="edit-section-title">📅 زمان‌بندی بازدید</h4>
            {loadingSchedules ? <p className="loading-text">در حال بارگذاری...</p> : (
              <div className="schedule-grid">
                <div className="schedule-header-row">
                  <div className="schedule-time-col" />
                  {WEEKDAYS.map(d => <div key={d} className="schedule-day-header">{DAY_LABELS[d]}</div>)}
                </div>
                {TIME_SLOTS.slice(0, -1).map((start, i) => (
                  <div key={start} className="schedule-row">
                    <div className="schedule-time-col">{start}–{TIME_SLOTS[i + 1]}</div>
                    {WEEKDAYS.map(d => (
                      <div key={d}
                        className={'schedule-cell' + (isSlotSelected(d, start) ? ' selected' : '')}
                        onClick={() => toggleSlot(d, start)} />
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="modal-cancel-button" onClick={onClose}>انصراف</button>
          <button className="modal-submit-button" onClick={handleSave} disabled={saving}>
            {saving ? 'در حال ذخیره...' : '💾 ذخیره تغییرات'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── PropertyListingsSection — listings + reservations for one property ───────

function PropertyListingsSection({ propertyId, onListingEdited }) {
  const [listings, setListings] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [editingListing, setEditingListing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all = await fetchPropertyListings();
      setListings(all.filter(l => {
        const pid = l.property && (l.property.id || l.property);
        return String(pid) === String(propertyId);
      }));
    } catch { /* ignore */ }
    setLoading(false);
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <p className="prop-loading">در حال بارگذاری آگهی‌ها...</p>;

  if (listings.length === 0) {
    return <p className="empty-state" style={{ fontSize: 12, padding: '8px 0' }}>هیچ آگهی‌ای برای این ملک ثبت نشده.</p>;
  }

  return (
    <div className="prop-listings-section">
      {listings.map(listing => {
        const isSale   = listing.listing_type === 'SALE';
        const lcStatus = listing.lifecycle_status || 'DRAFT';
        return (
          <div key={listing.id} className="prop-listing-card">
            <div className="prop-listing-header">
              <span className="prop-listing-type">{isSale ? '🏷 فروش' : '🔑 اجاره'}</span>
              <span className={'prop-listing-status status-badge status-' + lcStatus.toLowerCase()}>
                {LISTING_STATUS_LABEL[lcStatus] || lcStatus}
              </span>
              <button className="prop-btn prop-btn-edit" style={{ marginRight: 'auto' }}
                onClick={() => setEditingListing(listing)}>
                ویرایش
              </button>
            </div>

            <div className="prop-listing-details">
              {isSale ? (
                <>
                  <span> {listing.sale_details && listing.sale_details.asking_price
                    ? Number(listing.sale_details.asking_price).toLocaleString('fa-IR') + ' تومان'
                    : 'قیمت تعریف نشده'}</span>
                  {listing.sale_details && listing.sale_details.mortgage_status && (
                    <span>🏦 وام: {listing.sale_details.mortgage_status === 'AVAILABLE' ? 'دارد' : 'ندارد'}</span>
                  )}
                  {listing.sale_details && listing.sale_details.has_tenant !== undefined && (
                    <span>👤 {listing.sale_details.has_tenant ? 'دارای مستاجر' : 'خالی'}</span>
                  )}
                </>
              ) : (
                <>
                  <span>💵 اجاره: {listing.rent_details && listing.rent_details.monthly_rent
                    ? Number(listing.rent_details.monthly_rent).toLocaleString('fa-IR') + ' تومان'
                    : 'تعریف نشده'}</span>
                  <span>🔐 ودیعه: {listing.rent_details && listing.rent_details.deposit
                    ? Number(listing.rent_details.deposit).toLocaleString('fa-IR') + ' تومان'
                    : 'تعریف نشده'}</span>
                  {listing.rent_details && listing.rent_details.furnished_status && (
                    <span>🛋 {FURNISHED_MAP[listing.rent_details.furnished_status] || listing.rent_details.furnished_status}</span>
                  )}
                </>
              )}
            </div>

            <ReservationRequestsPanel listingId={listing.id} />
          </div>
        );
      })}

      {editingListing && (
        <ListingEditModal
          listing={editingListing}
          onClose={() => setEditingListing(null)}
          onSaved={() => { load(); if (onListingEdited) onListingEdited(); }}
        />
      )}
    </div>
  );
}

// ─── PropertyPanel ────────────────────────────────────────────────────────────

function PropertyPanel() {
  const navigate = useNavigate();
  const { properties, loading, error, removeProperty } = useProperties();
  const [focusLocation,    setFocusLocation]    = useState(null);
  const [expandedProperty, setExpandedProperty] = useState(null);

  const handleEdit = (property) => {
    navigate(property ? `/property-wizard?propertyId=${property.id}` : '/property-wizard');
  };

  const handleSelect = (property) => {
    const coords = property?.location?.coordinates;
    if (coords) setFocusLocation([coords[1], coords[0]]);
    setExpandedProperty(prev => (prev === property.id ? null : property.id));
  };

  if (loading) return <div className="prop-loading">در حال بارگذاری...</div>;
  if (error)   return <div className="prop-error">{error}</div>;

  return (
    <div className="prop-panel">
      <div className="prop-panel-header">
        <span className="prop-panel-title">املاک و آگهی‌های من</span>
        <button className="primary-btn prop-add-btn" onClick={() => handleEdit(null)}>
          + افزودن ملک
        </button>
      </div>

      {properties.length === 0 ? (
        <p className="empty-state">شما هیچ ملکی ثبت نکرده‌اید.</p>
      ) : (
        <div className="prop-grid" style={{ gridTemplateColumns: '1fr' }}>
          {properties.map(property => {
            const isExpanded = expandedProperty === property.id;
            const imageUrl   = property.primary_image || null;
            const hasLoc     = !!property.location?.coordinates;
            const lcStatus   = property.lifecycle_status || 'DRAFT';

            return (
              <div key={property.id} className={'prop-card prop-card-row' + (isExpanded ? ' prop-card-expanded' : '')}>
                {/* ── property summary row ── */}
                <div className="prop-card-summary" onClick={() => handleSelect(property)}
                  style={{ cursor: 'pointer' }}>
                  <div className="prop-card-img prop-card-img-sm">
                    {imageUrl
                      ? <img src={imageUrl} alt={property.name || 'ملک'} />
                      : <span className="prop-card-img-placeholder">🏠</span>}
                    {hasLoc && <span className="prop-card-loc-badge">📍</span>}
                  </div>
                  <div className="prop-card-body" style={{ flex: 1 }}>
                    <div className="prop-card-top">
                      <span className="prop-card-name">{property.name || ('ملک ' + String(property.id).slice(0, 8))}</span>
                      <span className={'prop-status prop-status-' + lcStatus.toLowerCase()}>
                        {PROP_STATUS_LABEL[lcStatus] || lcStatus}
                      </span>
                    </div>
                    {property.address && <div className="prop-card-addr">📍 {property.address}</div>}
                                   {property.area    && <div className="prop-card-meta">{property.area} م²</div>}
                  </div>
                  <div className="prop-card-actions" onClick={e => e.stopPropagation()}>
                    <button className="prop-btn prop-btn-edit" onClick={() => handleEdit(property)}>ویرایش</button>
                    <button className="prop-btn prop-btn-del"  onClick={() => removeProperty(property.id)}>حذف</button>
                  </div>
                  <div className="prop-expand-toggle" style={{ padding: '0 8px', color: '#6b7280', fontSize: 12 }}>
                    {isExpanded ? '▲ بستن' : '▼ آگهی‌ها'}
                  </div>
                </div>

                {/* ── expanded listings + reservations ── */}
                {isExpanded && (
                  <div className="prop-card-listings-panel">
                    <PropertyListingsSection propertyId={property.id} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="prop-map-wrapper">
        <MapContainerComponent focusLocation={focusLocation} />
      </div>
    </div>
  );
}

export default PropertyPanel;

